import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Chip, DataTable, type TableGroup } from '@/components/ui'
import type { SupportStage, TicketSummary } from '../api/types'
import { CATEGORY_SHORT, PRIORITY_LABELS, PRIORITY_VARIANT } from '../utils/meta'

export interface SupportTableProps {
  rows: TicketSummary[]
  stages: SupportStage[]
  onSelect: (c: TicketSummary) => void
  /** Optional row grouping (group-by-stage / assignee / category / priority). */
  groups?: TableGroup<TicketSummary>[]
  collapsedGroups?: ReadonlySet<string>
  onToggleGroup?: (key: string) => void
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function SupportTable({
  rows,
  stages,
  onSelect,
  groups,
  collapsedGroups,
  onToggleGroup,
}: SupportTableProps) {
  const stageName = useMemo(() => {
    const m = new Map<string, string>()
    for (const s of stages) m.set(s.id, s.name)
    return m
  }, [stages])

  const columns = useMemo<ColumnDef<TicketSummary, unknown>[]>(
    () => [
      {
        id: 'ticket_number',
        header: 'Ticket #',
        accessorKey: 'ticket_number',
        cell: (ctx) => (
          <span className="text-role-data-primary">{ctx.row.original.ticket_number}</span>
        ),
      },
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
        cell: (ctx) => (
          <span className="text-role-data-secondary" title={ctx.row.original.title}>
            {ctx.row.original.title}
          </span>
        ),
      },
      {
        id: 'partner_name',
        header: 'Customer',
        accessorFn: (r) => r.partner_name ?? '',
        cell: (ctx) => ctx.row.original.partner_name ?? '—',
      },
      {
        id: 'category',
        header: 'Category',
        accessorFn: (r) => CATEGORY_SHORT[r.category] ?? r.category,
        cell: (ctx) => CATEGORY_SHORT[ctx.row.original.category] ?? ctx.row.original.category,
      },
      {
        id: 'priority',
        header: 'Priority',
        accessorKey: 'priority',
        cell: (ctx) => (
          <Chip size="xs" variant={PRIORITY_VARIANT[ctx.row.original.priority]} dot>
            {PRIORITY_LABELS[ctx.row.original.priority]}
          </Chip>
        ),
      },
      {
        id: 'assigned_to',
        header: 'Assigned to',
        accessorFn: (r) => r.assigned_to_name ?? '',
        cell: (ctx) => ctx.row.original.assigned_to_name ?? 'Unassigned',
      },
      {
        id: 'sla_due_at',
        header: 'SLA due',
        accessorKey: 'sla_due_at',
        cell: (ctx) => formatDate(ctx.row.original.sla_due_at),
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (r) => (r.stage_id ? (stageName.get(r.stage_id) ?? '') : ''),
        cell: (ctx) =>
          ctx.row.original.stage_id
            ? (stageName.get(ctx.row.original.stage_id) ?? '—')
            : '—',
      },
    ],
    [stageName],
  )

  return (
    <DataTable
      data={rows}
      columns={columns}
      groups={groups}
      collapsedGroups={collapsedGroups}
      onToggleGroup={onToggleGroup}
      getRowId={(r) => r.id}
      onRowClick={onSelect}
      criticalRowPredicate={(r) => r.is_sla_breached}
      moduleId="support"
      maxHeight="100%"
      columnWidths={{
        ticket_number: 120,
        category: 130,
        priority: 110,
        sla_due_at: 120,
        stage: 160,
      }}
      minTableWidth={1150}
    />
  )
}
